import { AvailableStreamsCarousel } from 'components/availableStreamsCarousel';
import { BannerHeroRenderer } from 'components/banner/bannerHero';
import { BannerHTML5, enBannerHTML5 } from 'components/bannerHTML5';
import { HeaderTitleWithSearch } from 'components/headerTitleWithSearch';
import styles from 'components/page-content/sport-disciplina-manifestazione-avvenimento/PageContent.module.scss';
import { EmptyLive, Loader, useScommesseFallback } from 'components/scommessaFallback';
import { ScommesseAccordionFilterPrimary } from 'components/scommesseAccordionFilterPrimary/ScommesseAccordionFilterPrimary';
import { ScommesseConditionalAccordion } from 'components/scommesseConditionalAccordion';
import { ScommesseFilterItem, ScommesseFiltersList } from 'components/scommesseFiltersList';
import { ScommesseFiltersListScrollable } from 'components/scommesseFiltersListScrollable/ScommesseFiltersListScrollable';
import { ScommesseFiltersListSecondary } from 'components/scommesseFiltersListSecondary';
import { ScommesseFilterTertiary, toRegex } from 'components/scommesseFilterTertiary';
import { ItemType, ItemValueType } from 'components/scommesseFilterTertiary/ScommesseFilterTertiary';
import { ScommesseTitle } from 'components/scommesseTitle';
import { SportNews } from 'components/sportNews';
import { TabsWithAccordion } from 'components/tabsWithAccordion';
import { Wrapper } from 'components/wrapper';
import { AppFragment } from 'enums/path-fragment';
import { errorLabelsUpdated } from 'features/carrello/carrelloSlice';
import { selectIsSportSearchEnabled } from 'features/configuration/selectors';
import { LiveInternalLayout } from 'features/live/liveInternalLayout';
import { LIVE_STREAMING_ID, VideoStreamingMode } from 'features/live/liveVideoStreaming';
import { ScommesseLayout } from 'features/scommesseLayout';
import { selectIsVisualizzazioneEstesa } from 'features/settings/selectors';
import { setIsVisualizzazioneEstesa } from 'features/settings/settingsSlice';
import { decodePathname, getScommesse, selectGruppoList } from 'features/sport';
import { SportAvvenimentoHeader } from 'features/sport/components/sportAvvenimentoHeader';
import { SportComposer } from 'features/sport/components/sportComposer';
import { Taxes } from 'features/sport/components/sportTicket/sportTicketSlice';
import { SportTopNavigation } from 'features/sport/components/sportTopNavigation';
import { useFilterSelection, useInfoTemplateAvvenimento } from 'features/sport/hooks';
import { resetFilterAvvenimento, setIsInfoOpen } from 'features/sport/sportSlice';
import { useGetLabelsByKeys, useTranslation } from 'hooks/useLingUI';
import useScommessaListScrollable from 'hooks/useScommessaListScrollable';
import useWindowSize, { enComparer } from 'hooks/useWindowSize';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { LiveAvvenimentoPageQuery } from 'lib/datoCms/queries/getLiveAvvenimento';
import { TabsWithAccordionType } from 'lib/datoCms/queries/getSeoData';
import { AllCarrelloErrors } from 'lib/datoCms/types';
import { SportNewsDto } from 'lib/ssr/sport/news/types';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Category } from 'types/categoryEnum';
import { SportsCacheTemplateAvvenimentoDto, SportsTipoScommessa } from 'types/swagger';
import { breakpoint, isHappybetSite } from 'utility/constant';
import { isNotEmpty, isTruthy } from 'utility/functions';

type Props = {
  news?: SportNewsDto;
  taxes?: Taxes;
  seoData?: TabsWithAccordionType;
  allCarrelloErrors: AllCarrelloErrors[];
  liveAvvenimentoPage: LiveAvvenimentoPageQuery['liveDetailPage'];
};

const PageContent = ({ news, taxes, seoData, allCarrelloErrors, liveAvvenimentoPage }: Props) => {
  const dispatch = useAppDispatch();
  const { asPath, isFallback, isReady } = useRouter();
  const slugFromPath = decodePathname(asPath);

  const [scommessaGroupLabel, extendedVisualizationLabel, scommessaFilterBy, makeYourBetLabel, estesaSwitchLabel] =
    useGetLabelsByKeys([
      'scommessa-group',
      'extended-visualization',
      'scommessa-filter-by',
      'make-your-bet',
      'estesa-switch',
    ]);

  const { doCheck } = useWindowSize();
  const [isMobile, isDesktopXl] = doCheck([
    { comparer: enComparer.less, size: breakpoint.md },
    { comparer: enComparer.greater, size: breakpoint.xl },
  ]);

  const refContainer = useRef<HTMLDivElement>(null);

  const { showEmpty, showLoader } = useScommesseFallback({ container: refContainer.current, isReady });

  const [valueSecondaryFilter, _setValueSecondaryFilter] = useState<string>('');
  const [selectedPrimaryFilter, setSelectedPrimaryFilter] = useState<number>(0);
  const isVisualizzazioneEstesa = useTypedSelector(selectIsVisualizzazioneEstesa);
  const { templateAvvenimento, isTemplateLoading } = useInfoTemplateAvvenimento<SportsCacheTemplateAvvenimentoDto>();
  // stato filtri
  const [checkedItems, setCheckedItems] = useState<undefined | { [name: string]: boolean }>({});
  const [filterValue, setFilterValue] = useState<string | undefined>();

  const avvenimentoFilter = useTypedSelector(({ sport }) => sport.avvenimentoFilter);
  const isOpen = useTypedSelector(({ carrello }) => carrello.isOpen);
  const avvenimentoList = useTypedSelector(({ sport }) => sport.scommessa?.avvenimentoList);
  const infoTipoScommessaMap = useTypedSelector(({ sport }) => sport.scommessa?.infoTipoScommessaMap);

  const gruppoList = useTypedSelector(selectGruppoList);

  const selectedGruppo = useMemo(
    () => (isTemplateLoading ? undefined : gruppoList?.[selectedPrimaryFilter]),
    [gruppoList, isTemplateLoading, selectedPrimaryFilter]
  );

  const isSportSearchEnabled = useTypedSelector(selectIsSportSearchEnabled);
  const isMarcatore = useMemo(() => selectedGruppo?.isMarcatori ?? false, [selectedGruppo]);

  const { t } = useTranslation();
  const [sportTrKey, liveTrKey] = [AppFragment.Sport, AppFragment.Live];

  const refSlug = useRef('');

  useEffect(() => {
    const slug = selectedGruppo?.slug;
    if (slug && refSlug.current !== slug) {
      refSlug.current = slug;
      dispatch(getScommesse({ slug, isMarcatore }));
    }
  }, [dispatch, isMarcatore, selectedGruppo?.slug]);

  useEffect(() => {
    dispatch(errorLabelsUpdated(allCarrelloErrors));
  }, [dispatch, allCarrelloErrors]);

  // STATE TO HANDLE DAFAULT INFO OPENED
  useEffect(() => {
    dispatch(setIsInfoOpen(true));
    return () => {
      dispatch(setIsInfoOpen(false));
    };
  }, [dispatch]);

  const tipoScommessaList = useMemo(() => {
    let raw: Array<SportsTipoScommessa> | undefined;
    if (isTruthy(avvenimentoFilter?.length)) {
      // transform options to regex string
      const regex = toRegex(avvenimentoFilter);
      // get matches
      raw = selectedGruppo?.tipoScommessaList?.filter((tipoScommessa) =>
        tipoScommessa?.infoTipoScommessaKeyStringList?.some((info) => {
          const descrizione = `${infoTipoScommessaMap?.[info]?.descrizione}`;
          return isTruthy(descrizione.match(regex)?.length);
        })
      );
    } else {
      raw = gruppoList?.[selectedPrimaryFilter]?.tipoScommessaList;
    }

    return raw
      ?.map((e) => e!)
      .sort(
        (
          { priorita: prA, descrizione: desA, descrizioneTrKey: trkeyA },
          { priorita: prB, descrizione: desB, descrizioneTrKey: trKeyB }
        ) => {
          if (prA === prB) {
            return `${t(trKeyB, desB)}` > `${t(trkeyA, desA)}` ? -1 : 1;
          }
          return prA - prB;
        }
      );
  }, [
    avvenimentoFilter,
    selectedGruppo?.tipoScommessaList,
    infoTipoScommessaMap,
    gruppoList,
    selectedPrimaryFilter,
    t,
  ]);

  const filteredGruppoList: Array<ScommesseFilterItem> = useMemo(() => {
    if (gruppoList) {
      return gruppoList.map(({ descrizione, descrizioneTrKey, isNew }) => ({
        label: descrizione,
        hasNew: isNew,
        labelTrKey: descrizioneTrKey,
      }));
    }
    return [];
  }, [gruppoList]);

  const gruppoScommessaListScrollable = useScommessaListScrollable(filteredGruppoList);

  useEffect(() => {
    dispatch(resetFilterAvvenimento());
    setSelectedPrimaryFilter(0);
  }, [dispatch, slugFromPath]);

  useFilterSelection(filterValue ?? '', checkedItems ?? {});

  useEffect(() => {
    if (avvenimentoFilter.length === 0) {
      setCheckedItems({});
    }
  }, [avvenimentoFilter, selectedGruppo?.textualFilters]);

  useEffect(() => {
    if (valueSecondaryFilter) {
      tipoScommessaList?.filter((item) => item.descrizione === valueSecondaryFilter);
    }
  }, [valueSecondaryFilter, tipoScommessaList]);

  const onFilterClickHandler = (e: HTMLInputElement) => {
    setCheckedItems((prevCheckedItems) => ({
      ...prevCheckedItems,
      [e.name]: e.checked,
    }));
    setFilterValue(e.value);
  };

  const onGruppoClickHandler = (selectedFilter?: number) => {
    dispatch(resetFilterAvvenimento());
    setFilterValue(undefined);
    setCheckedItems({});
    setSelectedPrimaryFilter(selectedFilter ?? 0);
  };

  const { id, visualComposer, heroBanner, sportNewsBlock, guardaSuSnaiBlock } = liveAvvenimentoPage ?? {};

  const renderFilters = () => {
    const { descrizione, descrizioneTrKey, textualFilters } = selectedGruppo ?? {};

    if (isMobile)
      return (
        <React.Fragment>
          <ScommesseFiltersListScrollable
            selectedItem={selectedPrimaryFilter}
            color={'primary'}
            onSelectedElement={(index) => {
              setSelectedPrimaryFilter(index);
            }}
            className={styles.gruppoMobileContainer}
            list={gruppoScommessaListScrollable}
          />
          <ScommesseFilterTertiary
            className={styles.estesaSwitchWrapper}
            items={[{ label: estesaSwitchLabel, value: 'Estesa', checked: isVisualizzazioneEstesa }]}
            type={ItemType.toggle}
            clickAction={() => {
              dispatch(setIsVisualizzazioneEstesa(!isVisualizzazioneEstesa));
            }}
          />
          {isTruthy(selectedGruppo?.textualFilters?.length) && (
            <ScommesseFilterTertiary
              type={ItemType.checkbox}
              title={scommessaFilterBy}
              items={textualFilters as Array<ItemValueType>}
              classNameTitle={styles.wrapperCheckboxTitle}
              clickAction={onFilterClickHandler}
              checkedItem={
                checkedItems
                  ? checkedItems[`${textualFilters?.flatMap((filter) => filter.value)}` as keyof typeof checkedItems]
                  : false
              }
            />
          )}
        </React.Fragment>
      );

    return (
      <Wrapper bgColorTransparent noPaddingTop noPaddingLeft noPaddingBottom noPaddingRight={isOpen || isDesktopXl}>
        <div className={styles.wrapper}>
          <ScommesseTitle className={styles.text}>{makeYourBetLabel}</ScommesseTitle>
          <div className={styles.filtersContainer}>
            <ScommesseAccordionFilterPrimary
              label={scommessaGroupLabel}
              selectedFilter={descrizione}
              selectedFilterTrKey={descrizioneTrKey}
              hasShowMore={filteredGruppoList!.length > 8}
              hasMarginBotton
              isAccordionOpened={selectedPrimaryFilter > 8}
            >
              <ScommesseFiltersList
                items={filteredGruppoList}
                selectedIndex={selectedPrimaryFilter}
                onSelectedFilter={onGruppoClickHandler}
              />
            </ScommesseAccordionFilterPrimary>
          </div>
          <ScommesseFiltersListSecondary isOpen className={styles.scommesseFilterContainer}>
            {selectedGruppo && isTruthy(selectedGruppo?.textualFilters?.length) && (
              <ScommesseFilterTertiary
                type={ItemType.checkbox}
                title={scommessaFilterBy}
                items={textualFilters ?? []}
                checkedItem={
                  checkedItems
                    ? checkedItems[
                        `${textualFilters?.flatMap((singleFilter) => singleFilter.value)}` as keyof typeof checkedItems
                      ]
                    : false
                }
                className={styles.wrapperCheckbox}
                clickAction={onFilterClickHandler}
              />
            )}
            <ScommesseFilterTertiary
              type={ItemType.toggle}
              title={extendedVisualizationLabel}
              items={[{ label: estesaSwitchLabel, value: 'Estesa', checked: isVisualizzazioneEstesa }]}
              clickAction={() => {
                dispatch(setIsVisualizzazioneEstesa(!isVisualizzazioneEstesa));
              }}
            />
          </ScommesseFiltersListSecondary>
        </div>
      </Wrapper>
    );
  };

  const renderAvvenimentoHeader = () => {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    if (!templateAvvenimento) return <React.Fragment />;

    // const { live } = templateAvvenimento ?? {};

    let noPaddingRight = isOpen || isDesktopXl;
    if (isMobile) {
      noPaddingRight = true;
    }

    return (
      <React.Fragment>
        {<div id={`${VideoStreamingMode.minimal}-${LIVE_STREAMING_ID}`} />}
        <SportAvvenimentoHeader noPaddingRight={noPaddingRight} />
      </React.Fragment>
    );
  };

  return (
    <ScommesseLayout taxes={taxes} hasNoPaddingOnMain>
      <HeaderTitleWithSearch
        title={'Sport'}
        category={Category.Sport}
        titleTrKey={sportTrKey}
        labelSearch={isSportSearchEnabled ? 'Cerca' : null}
        titleHighlighted={'Live'}
        titleHighlightedTrKey={liveTrKey}
        isOnlyDesktop={true}
      />
      <BannerHeroRenderer pageId={id} heroBanner={heroBanner?.[0]} />
      <SportTopNavigation hasFirstElementFixed={!isHappybetSite} isLive={true} isOnlyDesktop={true} />
      <BannerHTML5 url={enBannerHTML5.Live} />
      <LiveInternalLayout
        hasStreaming={templateAvvenimento?.live?.hasStreaming}
        scommessaPreMatch={templateAvvenimento?.live?.scommessaPreMatch || undefined}
      >
        {renderAvvenimentoHeader()}
        <div ref={refContainer}>
          {!isFallback && isTruthy(gruppoList?.length) && selectedGruppo && (
            <React.Fragment>
              {renderFilters()}
              {infoTipoScommessaMap && avvenimentoList && (
                <Wrapper
                  bgColorTransparent
                  noPaddingTop
                  noPaddingLeft
                  noPaddingBottom
                  noPaddingRight={isMobile || isOpen || isDesktopXl}
                >
                  {tipoScommessaList?.map((tipoScommessa) => (
                    <ScommesseConditionalAccordion
                      key={tipoScommessa.key}
                      tipoScommessa={tipoScommessa}
                      avvenimentoKey={`${templateAvvenimento?.key}`}
                      isVisualizzazioneEstesa={isVisualizzazioneEstesa}
                    />
                  ))}
                </Wrapper>
              )}
              {visualComposer && (
                <SportComposer pageId={id} cmsData={visualComposer} templateSlug="templateAvvenimento" />
              )}
            </React.Fragment>
          )}
        </div>
        <Loader isVisible={showLoader} key={`loader-${id}-${showLoader}-${asPath}-${isReady}`} />
        <EmptyLive
          key={`empty-${id}-${showEmpty}-${asPath}-${isReady}`}
          isVisible={showEmpty}
          hasFilter={isNotEmpty(filterValue)}
          hasRedirect={!isTruthy(templateAvvenimento?.gruppoList?.length)}
          resetFilter={onGruppoClickHandler}
        />
        {guardaSuSnaiBlock && (
          <AvailableStreamsCarousel
            title={guardaSuSnaiBlock.title}
            className="inEvidenzaContainer"
            noPaddingRight={isMobile}
          />
        )}
        {sportNewsBlock && (
          <SportNews title={sportNewsBlock?.title ?? ''} url={sportNewsBlock?.url ?? ''} news={news ?? []} />
        )}
        <TabsWithAccordion tabs={seoData?.tabs ?? []} />
      </LiveInternalLayout>
    </ScommesseLayout>
  );
};

export default PageContent;
